import { ref, inject } from 'vue';

import { activeGiftCardKey } from '@/keys';

import * as giftcard from '@/connectors/litium/giftcard';
import * as config from '@/config';

import useContext from '@/composables/useContext';
import useExecuteCommand from '@/composables/useExecuteCommand';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useCartTracking from '@/composables/useCartTracking';

export default function () {
  const { cart } = useContext();
  const { executeCommand } = useExecuteCommand();
  const { websiteText } = useWebsiteTexts();
  const { reportRemoveFromCart } = useCartTracking();

  const requestPending = ref(false);
  const activeGiftCard = inject(activeGiftCardKey, ref());

  async function activateGiftCard({ codes, clear, clientAcceptedRedirect }: any) {
    try {
      requestPending.value = true;

      const response = await executeCommand(
        giftcard.commands.activateGiftCard,
        {
          codes,
          clear,
          clientAcceptedRedirect
        }
      );

      const { giftCardError } = response;

      if (giftCardError.error != 'NONE') {
        let errMsg: string;

        if (typeof giftCardError.error === 'string') {
          errMsg = giftCardError.error;
        } else if (giftCardError.error instanceof Error && giftCardError.error.message) {
          errMsg = giftCardError.message;
        } else {
          errMsg = 'unknown_error';
        }

       switch (errMsg) {
        case 'EXPIRED':
          throw websiteText(`giftcard__${errMsg.toLowerCase()}`, {validdate: giftCardError.validDate ?? ""}).value;
        case 'CONSUMED':
          throw giftCardError.validDate != undefined ? websiteText(`giftcard__${errMsg.toLowerCase()}_orderdate`, { orderdate: giftCardError.validDate ?? ""}).value : websiteText(`giftcard__${errMsg.toLowerCase()}`).value;        
        case 'WRONG_SITE':
          const linkHtml = giftCardError.validSiteUrl != undefined ? `<a href='${giftCardError.validSiteUrl}'>${giftCardError.validSiteUrl}</a>` : "";
          throw websiteText(`giftcard__${errMsg.toLowerCase()}`, { validsite: linkHtml }).value;
        default:
          throw websiteText(`giftcard__${errMsg.toLowerCase()}`).value;
        }
      }

      reportLoginEvent();

      return response;
    } 
    finally {
      requestPending.value = false;
    }
  }

  async function removeAllGiftCards() {
    try {
      requestPending.value = true;
      const previousRow = { ...(cart.value?.rows?.[0] || {}) };
      const response = await executeCommand(
        giftcard.commands.clearGiftCard,
        {}
      );

      reportRemoveFromCart(previousRow);

      return response;
    } catch (err) {
      console.error(err);
      throw websiteText('giftcard__activation_error').value;
    } finally {
      requestPending.value = false;
    }
  }

  async function placeRequest(body, method) {
    return await fetch(`${config.litiumBaseUrl}/api/giftcard-favorites/${method}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    });
  }

  function isFavoriteProduct(productId) {
    return activeGiftCard?.value?.favoriteProductIds?.includes(productId);
  }

  function ga4FavoriteProductEvent(eventName, giftcardId, productId) {
    if (typeof dataLayer === 'undefined') {
      return;
    }
    const dataToPush = {
      event: eventName,
      giftcardId: giftcardId,
      productId: productId,
    };

    dataLayer.push(dataToPush);
  }

  async function toggleFavoriteProduct(event, product) {
    event.preventDefault();
    event.stopPropagation();

    const giftCard = (!!activeGiftCard.value.giftCards && activeGiftCard.value.giftCards.length > 0) ? activeGiftCard.value.giftCards[0] : null;
    if (!giftCard) return;

    const articleNumber = product.articleNumber;
    const ids = activeGiftCard.value.favoriteProductIds;
    const requestBody = {
      GiftCardId: giftCard.code,
      ProductId: articleNumber
    };

    const wasFavorite = isFavoriteProduct(articleNumber);
    const action = wasFavorite ? "remove-favorite" : "add-favorite";

    // Optimistic, deduped local update so rapid clicks can't double-add and the
    // toggle guard reflects the new state immediately.
    if (wasFavorite) {
      const i = ids.indexOf(articleNumber);
      if (i > -1) ids.splice(i, 1);
    } else if (!ids.includes(articleNumber)) {
      ids.push(articleNumber);
    }

    try {
      await placeRequest(requestBody, action);
      ga4FavoriteProductEvent(action, giftCard.code, articleNumber);
    } catch (err) {
      // Roll back on failure
      if (wasFavorite) {
        if (!ids.includes(articleNumber)) ids.push(articleNumber);
      } else {
        const i = ids.indexOf(articleNumber);
        if (i > -1) ids.splice(i, 1);
      }
      console.error(err);
    }
  }

  return {
    requestPending,
    activateGiftCard,
    removeAllGiftCards,
    toggleFavoriteProduct,
    activeGiftCard
  };
}

declare var dataLayer: any;

function reportLoginEvent() {
  if (typeof dataLayer === 'undefined') {
    return;
  }
  const dataToPush = {
    event: 'login'
  };

  dataLayer.push(dataToPush);
}
